<!-- [title] als Bindung, NICHT als statisches Attribut: ein statisches title="…" bleibt zusaetzlich
     als DOM-Attribut am Element stehen, und der Browser zeigt daraus ueberall im Dialog seinen
     eigenen grauen Tooltip. Dieselbe Schreibweise nutzt delete-progress.html.

     Breite mitwachsend: `min(90vw, 1200px)`.
     - Der Deckel ist der Grund, warum es ueberhaupt "fest" wirken kann: oberhalb von rund 1330px
       Fensterbreite gewinnt immer er, darunter der vw-Anteil. Beim ersten Versuch lag er bei
       1040px, also unterhalb der ueblichen Fensterbreiten - dadurch aenderte sich beim Ziehen nie
       etwas.
     - Ein Deckel bleibt noetig: eine Dateiliste ueber die volle Breite eines grossen Monitors
       liest sich nicht mehr, die Augen springen zu weit zwischen Name und Aktionen. -->
<bic-popup (close)="closeClicked()" [title]="'Papierkorb'" customClass="w-[min(90vw,1200px)]">
  <div bicPopupContent class="flex flex-col gap-4">
    <span class="text-sm text-gray">
      Gelöschte Dateien werden {{ retentionDays }} Tage aufbewahrt und danach automatisch endgültig
      entfernt. Ein gelöschter Ordner ist ein Eintrag – sein Inhalt kommt beim Wiederherstellen mit.
    </span>

    @if (loading) {
      <span class="text-sm text-gray">Papierkorb wird geladen …</span>
    } @else if (entries.length === 0) {
      <span class="text-sm text-gray">Der Papierkorb ist leer.</span>
    } @else {
      <div class="flex flex-col gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <span class="text-xs font-medium text-gray-500">
          {{ entries.length }} {{ entries.length === 1 ? 'Eintrag' : 'Einträge' }}
        </span>

        <!-- Grid statt Flex je Zeile: nur so stehen die Aktionen wirklich in einer Spalte. Bei
             flex-Zeilen richtet sich ihre Position nach der Laenge des Dateinamens, und die
             Aktionen sassen von Zeile zu Zeile versetzt. Die beiden auto-Spalten nehmen die Breite
             ihres breitesten Inhalts an und gelten damit fuer ALLE Zeilen gleich.

             Die Hoehe waechst mit dem Fenster (`min(70vh, 44rem)`) statt bei festen 18rem zu
             stoppen - auf einem hohen Bildschirm waren sonst vier Eintraege sichtbar und der Rest
             hinter einem Rollbalken, obwohl darunter Platz frei blieb. -->
        <!-- overflow-x-hidden ist kein Beiwerk: `overflow-y-auto` allein laesst die X-Achse nicht
             auf `visible` stehen, sondern die CSS-Regeln machen daraus `auto`. Jede Kleinigkeit,
             die ueber den rechten Rand hinausragt, wird damit zum Rollbalken statt still
             abgeschnitten zu werden. Waagerecht ist hier nichts zu scrollen: die Namensspalte ist
             `minmax(0,1fr)` und kuerzt mit `truncate`, die beiden anderen sind 36px breit. -->
        <div class="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1
                    max-h-[min(70vh,44rem)] overflow-y-auto overflow-x-hidden">
          @for (entry of entries; track entry.id) {
            <div class="flex items-center gap-3 min-w-0 py-1.5">
              <fa-icon [icon]="entry.isFolder ? faFolder : faFile" class="text-gray-400 w-4 shrink-0"></fa-icon>
              <div class="flex flex-col min-w-0">
                <!-- Zwei Graustufen plus zwei Schriftgroessen: der Name ist der Inhalt, das Datum
                     die Beiwerk-Zeile. Vorher lagen beide bei aehnlichem Grau und gleicher Groesse,
                     dadurch gab es keine Hierarchie. -->
                <span [tooltip]="entry.name ?? ''" class="text-sm text-gray-700 truncate">
                  {{ entry.name }}
                </span>
                <!-- `truncate` statt nur `whitespace-nowrap`: der Umbruch muss weg bleiben (sonst
                     rutscht "endgültig am 23.09.2026" in eine dritte Zeile und die Zeilenhoehen
                     werden ungleich), aber nowrap ALLEIN laesst den Text bei schmalem Dialog
                     einfach unter den Aktionssymbolen hindurchlaufen. `truncate` bringt beides:
                     nowrap plus overflow-hidden plus "…" - wie schon beim Namen darueber. -->
                <span class="text-xs text-gray-400 truncate">
                  Gelöscht {{ entry.deletedAt | date: 'dd.MM.yyyy, HH:mm' }}
                  @if (!entry.usedInCheck && entry.purgeAfter) {
                    · endgültig {{ entry.purgeAfter | date: 'dd.MM.yyyy' }}
                  }
                </span>
              </div>
            </div>

            <!-- Aktionen als Symbole statt beschrifteter Knoepfe: zwei Textknoepfe je Zeile
                 ("Wiederherstellen" / "Endgültig löschen") nahmen mehr Platz ein als der
                 Dateiname, um den es eigentlich geht. Jedes Symbol traegt Tooltip UND aria-label -
                 ohne Beschriftung ist beides Pflicht, nicht Zierde. -->
            @if (busyIds.has(entry.id ?? '')) {
              <!-- Der Kreisel steckt in einem 36px-Feld wie die Knoepfe daneben, und NUR das Symbol
                   darin dreht sich. Vorher drehte sich das Feld selbst: ein rotierendes Kastenmass
                   zaehlt mit seinen Ecken in den scrollbaren Bereich (die Diagonale ist um den
                   Faktor Wurzel 2 laenger), und weil das Feld direkt am rechten Rand sitzt, ragte
                   es dort heraus. Die Liste bekam dadurch fuer die Dauer der Aktion einen
                   horizontalen Rollbalken - sichtbar nur waehrend des Loeschens, deshalb so schwer
                   zu fassen. Das kleinere Symbol dreht sich innerhalb des Feldes und bleibt drin. -->
              <span [tooltip]="'Wird ausgeführt …'" aria-label="Wird ausgeführt"
                    class="col-span-2 justify-self-end w-9 h-9 flex items-center justify-center
                           text-gray-400" role="img">
                <fa-icon [icon]="faSpinnerThird" class="text-lg animate-spin"></fa-icon>
              </span>
            } @else {
              <button (click)="restore(entry)" [tooltip]="'Wiederherstellen'"
                      aria-label="Wiederherstellen"
                      class="w-9 h-9 flex items-center justify-center rounded-md text-gray-400
                             hover:text-gray-700 hover:bg-gray-100" type="button">
                <fa-icon [icon]="faTrashArrowUp" class="text-lg"></fa-icon>
              </button>

              <!-- Kein Loeschen-Symbol, wenn eine Pruefung die Datei braucht: das Backend lehnt es
                   ab, und ein Knopf, der nichts tut, ist schlechter als keiner. Das Schloss ist ein
                   Zustands-Kennzeichen und bewusst kein deaktivierter Knopf - hier ist nichts zu
                   tun, auch spaeter nicht. -->
              @if (entry.usedInCheck) {
                <span [tooltip]="protectedHint" aria-label="Geschützt"
                      class="w-9 h-9 flex items-center justify-center text-gray-400" role="img">
                  <fa-icon [icon]="faLock" class="text-lg"></fa-icon>
                </span>
              } @else {
                <button (click)="purge(entry)" [tooltip]="'Endgültig löschen'"
                        aria-label="Endgültig löschen"
                        class="w-9 h-9 flex items-center justify-center rounded-md text-gray-400
                               hover:text-red hover:bg-red-50" type="button">
                  <fa-icon [icon]="faTrashXmark" class="text-lg"></fa-icon>
                </button>
              }
            }
          }
        </div>
      </div>
    }

    <!-- Die Rueckfrage steht dort, wo eben noch der ausloesende Knopf war - und nennt die Zahl,
         um die es geht. "Wirklich?" ohne Zahl ist die Art von Rueckfrage, die man wegklickt. -->
    @if (confirmEmpty) {
      <span class="text-sm text-gray-800">
        {{ purgeableEntries.length }} {{ purgeableEntries.length === 1 ? 'Eintrag wird' : 'Einträge werden' }} zur
        endgültigen Löschung vorgemerkt. Das lässt sich nicht rückgängig machen.
        @if (protectedCount > 0) {
          {{ protectedCount }} {{ protectedCount === 1 ? 'Eintrag' : 'Einträge' }} bleib{{ protectedCount === 1 ? 't' : 'en' }}
          erhalten, weil {{ protectedCount === 1 ? 'er' : 'sie' }} von einer Prüfung verwendet
          {{ protectedCount === 1 ? 'wird' : 'werden' }}.
        }
      </span>
    }

    @if (message) {
      <span class="text-sm text-gray">{{ message }}</span>
    }
  </div>

  <div bicPopupButtonGroup class="flex flex-row w-full justify-end gap-4">
    @if (confirmEmpty) {
      <bic-button (onClick)="cancelEmpty()" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="emptyTrash()" buttonType="primary">Endgültig löschen</bic-button>
    } @else {
      <!-- Nur anbieten, wenn es etwas zu leeren gibt: bei einem leeren Papierkorb - oder einem, in
           dem nur geschuetzte Eintraege liegen - waere es ein Knopf ohne Wirkung. -->
      @if (!loading && purgeableEntries.length > 0) {
        <bic-button (onClick)="askEmpty()" [disabled]="emptying" buttonType="white">
          Papierkorb leeren
        </bic-button>
      }
      <bic-button (onClick)="closeClicked()" [disabled]="emptying" buttonType="white">Schließen</bic-button>
    }
  </div>
</bic-popup>
